'use client'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { FormField, FormLabel } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/services/providers/auth-provider'
import { cn } from '@/services/utils/cn'
import { format } from 'date-fns'
import { th } from 'date-fns/locale/th'
import { CalendarIcon, ChevronDownIcon } from 'lucide-react'
import { Control } from 'react-hook-form'
import { FormSchema } from '../sidebar-content'

export function Personal({ control }: { control: Control<FormSchema> }) {
  const auth = useAuth()

  return (
    <>
      <>
        <label>Username</label>
        <Input
          value={auth?.user?.username}
          readOnly
          disabled
          placeholder="ชื่อผู้ใช้"
          className="bg-gray-200"
        />
      </>
      <FormField
        control={control}
        name="name_en"
        render={({ field }) => (
          <>
            <FormLabel>First Name (EN)</FormLabel>
            <Input
              {...field}
              value={field.value ?? ''}
              placeholder="ชื่อภาษาอังกฤษ"
            />
          </>
        )}
      />
      <FormField
        control={control}
        name="surname_en"
        render={({ field }) => (
          <>
            <FormLabel>Last Name (EN)</FormLabel>
            <Input
              {...field}
              value={field.value ?? ''}
              placeholder="นามสกุลภาษาอังกฤษ"
            />
          </>
        )}
      />
      <FormField
        control={control}
        name="birthdate"
        render={({ field, fieldState }) => (
          <>
            <FormLabel>Date of birth</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={'outline'}
                  className={cn(
                    'w-full max-w-[280px] justify-between bg-transparent text-left font-normal',
                    !field.value && 'text-muted-foreground'
                  )}
                >
                  <div className="flex content-center">
                    <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                    {field.value ? (
                      <span className="font-normal text-zinc-950">
                        {format(field.value, 'dd-MM-yyyy', {
                          locale: th,
                        })}
                      </span>
                    ) : (
                      <span>เลือกวัน-เดือน-ปี</span>
                    )}
                  </div>
                  <ChevronDownIcon />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="relative z-[200] w-auto p-0"
                align="start"
              >
                <Calendar
                  mode="single"
                  enableMonthYearPicker
                  selected={new Date(field.value ?? '')}
                  onSelect={(newDate) => {
                    field.onChange(
                      newDate
                        ? new Date(
                            newDate.getTime() -
                              newDate.getTimezoneOffset() * 60000
                          ).toISOString()
                        : null
                    )
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <span className="text-xs text-red-500" aria-live="polite">
              {fieldState.error?.message}
            </span>
          </>
        )}
      />
      <FormField
        control={control}
        name="address"
        render={({ field }) => (
          <>
            <FormLabel>Address</FormLabel>
            <Input {...field} value={field.value ?? ''} placeholder="ที่อยู่" />
          </>
        )}
      />

      <label>Objective</label>
      <Select value={auth.user?.user_purpose ?? 'ANY'}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="จุดประสงค์" />
        </SelectTrigger>
        <SelectContent className="z-[200]">
          <SelectItem value="ANY">จุดประสงค์</SelectItem>
          <SelectItem value="PARENT">ผู้ปกครองหาให้บุตรหลาน</SelectItem>
          <SelectItem value="TEACHER">อาจารย์หาให้ลูกศิษย์</SelectItem>
          <SelectItem value="COMPETITION">นักแข่งหาให้ตนเอง</SelectItem>
        </SelectContent>
      </Select>
    </>
  )
}
